import { pickCurrentFlightTicket } from "./flightGrounding";
import { pickCurrentTicket, sortOpsBoard } from "./opsBoard";
import {
  attemptGenerateOfp,
  dispatchUrl,
  fieldsForLeg,
  simbriefAirlineCode,
  simbriefCargoParam,
  simbriefExtraFreightLb,
  staticIdFromMissionId,
  type SimBriefFields,
} from "./simbrief";
import { ballparkTripWeight } from "./opsConstraints";
import { hydrateOpsDeskSettings } from "./opsDeskSettings";
import { ensureLegs } from "./schedule";
import type { AircraftSpec, CharterRequest, CompanyProfile, DraftMission, Mission } from "./types";

/** Mission draft plus SimBrief-shaped fields for a later OFP push. */
export type FlightPlanDraft = {
  requestId?: string;
  cargoLb: number;
  mission: DraftMission;
  simbrief: SimBriefFields;
  dispatchUrl: string;
};

export function flightPlanFromDraft(
  mission: DraftMission,
  opts: {
    fleet?: AircraftSpec[];
    username?: string;
    cargoLb?: number;
    requestId?: string;
    captain?: string;
    company?: Partial<CompanyProfile> | null;
  } = {},
): FlightPlanDraft {
  const fleet = opts.fleet ?? [];
  const legs = ensureLegs(mission);
  const leg = legs[0];
  const cargoLb = opts.cargoLb ?? mission.cargoLb ?? 0;
  const companyPrefix = opts.company?.flightNumberPrefix;
  if (!leg) {
    const empty: SimBriefFields = {
      orig: mission.originIcao,
      dest: mission.destIcao,
      type: "",
      reg: mission.registration,
      airline: simbriefAirlineCode(companyPrefix),
      fltnum: "80",
      callsign: mission.callsign.replace(/\s+/g, ""),
      pax: String(mission.paxCount),
      date: "",
      deph: "",
      depm: "",
      units: "LBS",
      navlog: "1",
      notams: "1",
      maps: "detail",
      acdata: "",
      cpt: "",
      steh: "1",
      stem: "00",
      resvrule: "45",
      static_id: mission.id ? staticIdFromMissionId(mission.id) : undefined,
      cargo: simbriefCargoParam(simbriefExtraFreightLb(mission.paxCount, cargoLb, mission.flightType)),
    };
    return { requestId: opts.requestId, cargoLb, mission, simbrief: empty, dispatchUrl: dispatchUrl(empty) };
  }
  const simbrief = fieldsForLeg(mission, leg, {
    username: opts.username,
    registration: mission.registration,
    captain: opts.captain,
    fleet,
    cargoLb,
    companyPrefix,
  });
  return {
    requestId: opts.requestId,
    cargoLb,
    mission,
    simbrief,
    dispatchUrl: dispatchUrl(simbrief),
  };
}

/** Prefill Megan and Flight plans both open. Cargo is extra freight only. */
export type SimbriefGeneratePlan = {
  url: string;
  legacyUrl: string;
  utcLabel: string;
  orig: string;
  dest: string;
  type: string;
  reg: string;
  pax: string;
  /** SimBrief cargo param: extra freight in thousands of pounds. `0` is explicit. */
  cargo: string;
  staticId: string;
  /** Set when the ballpark is over a limit and Settings say flag, not block. */
  weightWarning?: string;
};

export function simbriefGenerateForMission(
  mission: DraftMission,
  opts: {
    fleet?: AircraftSpec[];
    username?: string;
    cargoLb?: number;
    captain?: string;
    company?: Partial<CompanyProfile> | null;
  } = {},
): { ok: true; plan: SimbriefGeneratePlan } | { ok: false; error: string } {
  const draft = flightPlanFromDraft(mission, opts);
  let weightWarning: string | undefined;
  if (opts.company) {
    const fleet = opts.fleet ?? [];
    const spec = fleet.find((row) => row.id === mission.aircraftId);
    const weight = ballparkTripWeight({
      ops: hydrateOpsDeskSettings(opts.company.opsDesk),
      limits: spec ?? {},
      paxCount: mission.paxCount,
      cargoLb: opts.cargoLb ?? mission.cargoLb ?? 0,
      fuelLb: mission.fuelLbs ?? 0,
      flightType: mission.flightType,
    });
    if (weight.severity === "block" && !mission.weightOverride) return { ok: false, error: weight.message };
    if (weight.severity === "flag" || (weight.severity === "block" && mission.weightOverride)) {
      weightWarning = mission.loadWarning || weight.message;
    }
  }
  const generated = attemptGenerateOfp(draft.simbrief);
  if (!generated.ok) return { ok: false, error: generated.message };
  return {
    ok: true,
    plan: {
      url: generated.url,
      legacyUrl: generated.legacyUrl,
      utcLabel: generated.utcLabel,
      orig: draft.simbrief.orig,
      dest: draft.simbrief.dest,
      type: draft.simbrief.type,
      reg: draft.simbrief.reg,
      pax: draft.simbrief.pax,
      cargo: draft.simbrief.cargo ?? "0",
      staticId: draft.simbrief.static_id ?? staticIdFromMissionId(mission.id),
      weightWarning,
    },
  };
}

export function isOpeningFerryJob(row: Pick<CharterRequest, "flightType" | "clientName" | "id" | "emailSubject">): boolean {
  if (row.flightType === "ferry") return true;
  if (/^company ferry$/i.test(row.clientName ?? "")) return true;
  if ((row.id ?? "").startsWith("inq-career-")) return true;
  return /^\s*FERRY\b/i.test(row.emailSubject ?? "");
}

export type FlightPlanTicket =
  | { kind: "mission"; id: string; originIcao: string; destIcao: string; mission: DraftMission | Mission }
  | { kind: "inbox"; id: string; originIcao: string; destIcao: string; request: CharterRequest };

/**
 * Flight Plans ticket picker. Filed missions win when focused / pending;
 * the Career opening ferry inbox job is used when no mission is on file yet
 * so Request new plan does not fall back to home–home.
 */
export function pickFlightPlanTicket(
  missions: Array<DraftMission | Mission>,
  inbox: CharterRequest[] | undefined,
  focusId?: string | null,
): FlightPlanTicket | null {
  const focus = focusId?.trim() || "";
  if (focus) {
    const mission = missions.find((m) => m.id === focus);
    if (mission) {
      return {
        kind: "mission",
        id: mission.id,
        originIcao: mission.originIcao,
        destIcao: mission.destIcao,
        mission,
      };
    }
    const request = inbox?.find((r) => r.id === focus);
    if (request) {
      return {
        kind: "inbox",
        id: request.id,
        originIcao: request.originIcao,
        destIcao: request.destIcao,
        request,
      };
    }
  }

  const current =
    pickCurrentFlightTicket({ missions: missions as Mission[] }) ?? pickCurrentTicket({ missions: missions as Mission[] });
  const pending = current ?? sortOpsBoard(missions as Mission[]).find((m) => m.status === "pending" || m.status === "in-progress");
  const firstMission = pending ?? sortOpsBoard(missions as Mission[])[0];
  if (firstMission) {
    return {
      kind: "mission",
      id: firstMission.id,
      originIcao: firstMission.originIcao,
      destIcao: firstMission.destIcao,
      mission: firstMission,
    };
  }

  const opening = (inbox ?? []).find(
    (r) => r.status !== "declined" && !r.acceptedMissionId && isOpeningFerryJob(r),
  );
  if (!opening) return null;
  return {
    kind: "inbox",
    id: opening.id,
    originIcao: opening.originIcao,
    destIcao: opening.destIcao,
    request: opening,
  };
}
